import { useEffect, useMemo, useState } from "react";

import {

  AppButton,

  AppTable,

  AppTableCell,

  AppTableHeadCell,

  AppTableRow,

  FilterBar,

  RowNumberCell,

  StageStatusChip,

} from "../../../../components/ui";

import { Box, InputAdornment, Paper, Stack, TextField, Tooltip, Typography } from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import dayjs from "dayjs";

import utc from "dayjs/plugin/utc";

import {

  AttorneyLeadStatus,

  AttorneyLeadsResponse,

  Category,

} from "../../../../api";

import {

  CategoryMapping,

  LeadType,

} from "../../LeadsManagement/shared/Constants";

import {
  ATTORNEY_STAGE_FILTER_ORDER,
  getAttorneyStageFilterLabel,
  getAttorneyStageLabel,
} from "../../LeadsManagement/shared/attorneyStageLabels";
import {
  buildCaseStatusFields,
  getCompactCaseStatusLabel,
} from "../../LeadsManagement/LeadDetails/CaseStatusSummary";

import { HelpersService } from "../../../../services/Helpers";

import useNavigateSearch from "../../../../services/useNavigateSearch";



dayjs.extend(utc);



const DEFAULT_ROWS_PER_PAGE = 50;



const STAGE_FILTERS: {
  status: AttorneyLeadStatus | null;
  label: string;
}[] = [
  { status: null, label: "All" },
  ...ATTORNEY_STAGE_FILTER_ORDER.map((status) => ({
    status,
    label: getAttorneyStageFilterLabel(status),
  })),
];



const TABLE_COLUMNS = [

  { id: "status", label: "Your stage", sortable: true },

  { id: "leadName", label: "Lead name", sortable: true },

  { id: "employer", label: "Employer", sortable: true },

  { id: "category", label: "Category", sortable: true },

  { id: "caseStatus", label: "Case status", sortable: false },

  { id: "terminationDate", label: "Termination", sortable: false },

  { id: "updatedAt", label: "Updated", sortable: true },

];



type Props = {

  attorneyId?: number | null;

  attorneyName?: string;

  attorneyLeads?: Record<string, AttorneyLeadsResponse[] | null> | null;

};



type CaseRow = AttorneyLeadsResponse & {

  leadName: string;

  displayName: string;

};



type SortKey = "status" | "leadName" | "employer" | "category" | "updatedAt";

type Order = "asc" | "desc";



function flattenAttorneyLeads(

  attorneyLeads?: Record<string, AttorneyLeadsResponse[] | null> | null

): CaseRow[] {

  if (!attorneyLeads) {

    return [];

  }



  return Object.values(attorneyLeads)

    .flatMap((leads) => leads ?? [])

    .map((lead) => {

      const leadName =

        `${lead.firstName ?? ""} ${lead.lastName ?? ""}`.trim();

      return {

        ...lead,

        leadName,

        displayName: getLeadDisplayName(lead, leadName),

      };

    });

}



function getLeadDisplayName(

  lead: AttorneyLeadsResponse,

  leadName: string

): string {

  if (lead.isFullSummary) {

    return leadName || "—";

  }

  return lead.withoutPayment ? "Hidden until agreement signed" : "Hidden until paid";

}



function formatCategory(category?: Category) {

  if (!category) return "—";

  return CategoryMapping[category] ?? HelpersService.camelToString(category);

}



function getSortValue(row: CaseRow, key: SortKey): string | number {

  switch (key) {

    case "status":

      return (row.status ?? "").toLowerCase();

    case "leadName":

      return row.displayName.toLowerCase();

    case "employer":

      return (row.employer ?? "").toLowerCase();

    case "category":

      return formatCategory(row.category).toLowerCase();

    case "updatedAt":

      return row.updatedAt ? dayjs.utc(row.updatedAt).valueOf() : 0;

    default:

      return "";

  }

}



function compareCases(a: CaseRow, b: CaseRow, orderBy: SortKey, order: Order) {

  const aValue = getSortValue(a, orderBy);

  const bValue = getSortValue(b, orderBy);



  let result = 0;

  if (typeof aValue === "number" && typeof bValue === "number") {

    result = aValue - bValue;

  } else {

    result = String(aValue).localeCompare(String(bValue), undefined, {

      sensitivity: "base",

      numeric: true,

    });

  }



  return order === "asc" ? result : -result;

}



export default function AttorneyCasesTable({

  attorneyId,

  attorneyName,

  attorneyLeads,

}: Props) {

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedStage, setSelectedStage] = useState<AttorneyLeadStatus | null>(

    null

  );

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [orderBy, setOrderBy] = useState<SortKey>("updatedAt");

  const [order, setOrder] = useState<Order>("desc");

  const navigate = useNavigateSearch();



  const allCases = useMemo(

    () => flattenAttorneyLeads(attorneyLeads),

    [attorneyLeads]

  );



  const countByStatus = useMemo(() => {

    const counts: Partial<Record<AttorneyLeadStatus, number>> = {};

    allCases.forEach((row) => {

      if (row.status) {

        counts[row.status] = (counts[row.status] ?? 0) + 1;

      }

    });

    return counts;

  }, [allCases]);



  const filteredCases = useMemo(() => {

    let list = allCases;



    if (selectedStage) {

      list = list.filter((row) => row.status === selectedStage);

    }



    if (searchTerm.trim()) {

      const lower = searchTerm.toLowerCase();

      list = list.filter(

        (row) =>

          row.displayName.toLowerCase().includes(lower) ||

          (row.employer ?? "").toLowerCase().includes(lower) ||

          formatCategory(row.category).toLowerCase().includes(lower)

      );

    }



    return [...list].sort((a, b) => compareCases(a, b, orderBy, order));

  }, [allCases, selectedStage, searchTerm, orderBy, order]);



  const maxPage = Math.max(

    0,

    Math.ceil(filteredCases.length / rowsPerPage) - 1

  );

  const safePage = Math.min(page, maxPage);



  useEffect(() => {

    if (page > maxPage) {

      setPage(maxPage);

    }

  }, [page, maxPage]);



  const paginatedCases = useMemo(

    () =>

      filteredCases.slice(

        safePage * rowsPerPage,

        safePage * rowsPerPage + rowsPerPage

      ),

    [filteredCases, safePage, rowsPerPage]

  );



  const handleStageChange = (status: AttorneyLeadStatus | null) => {

    setSelectedStage(status);

    setPage(0);

  };



  const handleRequestSort = (columnId: string) => {

    if (columnId === "terminationDate") return;

    const property = columnId as SortKey;

    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");

    setOrderBy(property);

    setPage(0);

  };



  const handleOpenLead = (leadId?: number) => {

    if (!leadId) {

      return;

    }

    navigate(`/lead/${leadId}`, { leadType: LeadType.Admin, isEdit: "false" });

  };



  const handleOpenFullTable = () => {

    if (!attorneyId) {

      navigate("/attorney-leads");

      return;

    }

    navigate(`/attorney-leads?attorneyId=${attorneyId}`);

  };



  const clearFilters = () => {

    setSearchTerm("");

    setSelectedStage(null);

    setPage(0);

  };



  const filtersActive = !!selectedStage || !!searchTerm.trim();



  return (

    <Box sx={{ width: "100%", minWidth: 0 }}>

      <Stack

        direction={{ xs: "column", sm: "row" }}

        justifyContent="space-between"

        alignItems={{ xs: "stretch", sm: "center" }}

        spacing={1}

        sx={{ mb: 2 }}

      >

        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>

          <strong>Your stage</strong> is this attorney&apos;s progress (Accepted → Won → Agreement signed → Paid).

          It is separate from the referral pipeline status shown on the lead detail page.

          {allCases.length > 0 && (

            <> Showing {filteredCases.length.toLocaleString()} of {allCases.length.toLocaleString()} cases.</>

          )}

        </Typography>

        {attorneyId && (

          <AppButton

            variant="outlined"

            color="primary"

            size="small"

            startIcon={<OpenInNewIcon />}

            onClick={handleOpenFullTable}

            sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "center" } }}

          >

            Open paginated leads table

          </AppButton>

        )}

      </Stack>



      <Box sx={{ mb: 2 }}>

        <FilterBar

          hideFilterButton

          filtersActive={filtersActive}

          clearDisabled={!filtersActive}

          onClear={clearFilters}

          chips={

            selectedStage

              ? [

                  {

                    key: "stage",

                    label: `Stage: ${getAttorneyStageFilterLabel(selectedStage)}`,

                    onDelete: () => handleStageChange(null),

                  },

                ]

              : []

          }

          leading={

            <TextField

              size="small"

              placeholder="Search name, employer, or category…"

              value={searchTerm}

              onChange={(e) => {

                setSearchTerm(e.target.value);

                setPage(0);

              }}

              InputProps={{

                startAdornment: (

                  <InputAdornment position="start">

                    <SearchIcon fontSize="small" color="action" />

                  </InputAdornment>

                ),

              }}

              sx={{

                width: { xs: "100%", sm: 360 },

                bgcolor: "background.paper",

                borderRadius: 1,

              }}

            />

          }

        />

      </Box>



      <Paper

        variant="outlined"

        sx={{ px: 1.5, py: 1, mb: 2, bgcolor: "#fafbfd" }}

      >

        <Stack

          direction="row"

          spacing={1}

          flexWrap="wrap"

          useFlexGap

          alignItems="center"

        >

          {STAGE_FILTERS.map(({ status, label }) => {

            const count =

              status === null ? allCases.length : countByStatus[status] ?? 0;

            const isSelected = selectedStage === status;



            if (status === null) {

              return (

                <AppButton

                  key="all"

                  variant={isSelected ? "contained" : "outlined"}

                  color={isSelected ? "primary" : "darkGray"}

                  size="small"

                  onClick={() => handleStageChange(null)}

                >

                  {label} ({count})

                </AppButton>

              );

            }



            return (

              <StageStatusChip

                key={status}

                status={status}

                label={`${label} (${count})`}

                selected={isSelected}

                onClick={() => handleStageChange(status)}

              />

            );

          })}

        </Stack>

      </Paper>



      <AppTable

        columns={TABLE_COLUMNS}

        showRowNumber

        sortBy={orderBy}

        sortAsc={order === "asc"}

        onRequestSort={handleRequestSort}

        isEmpty={filteredCases.length === 0}

        emptyTitle={

          allCases.length === 0

            ? attorneyName

              ? `No cases found for ${attorneyName}.`

              : "No cases found."

            : "No cases match the selected filters."

        }

        extraColumnCount={1}

        trailingHeadCells={

          <AppTableHeadCell align="right" sx={{ width: 120 }}>

            Actions

          </AppTableHeadCell>

        }

        pagination={{

          count: filteredCases.length,

          page: safePage,

          rowsPerPage,

          rowsPerPageOptions: [25, 50, 100, 200],

          onPageChange: setPage,

          onRowsPerPageChange: (next) => {

            setRowsPerPage(next);

            setPage(0);

          },

        }}

      >

        {paginatedCases.map((row, index) => (

          <AppTableRow

            tabIndex={-1}

            key={row.id}

            onClick={() => handleOpenLead(row.id)}

            sx={{ cursor: "pointer" }}

          >

            <RowNumberCell value={safePage * rowsPerPage + index + 1} />

            <AppTableCell align="left" sx={{ minWidth: 160 }}>

              <StageStatusChip

                status={row.status}

                label={getAttorneyStageLabel(row.status, row.withoutPayment)}

                onClick={(e) => e.stopPropagation()}

              />

            </AppTableCell>

            <AppTableCell align="left" sx={{ minWidth: 140 }}>

              {row.displayName}

            </AppTableCell>

            <AppTableCell align="left" sx={{ minWidth: 140 }}>

              {row.employer?.trim() || "—"}

            </AppTableCell>

            <AppTableCell align="left">

              {formatCategory(row.category)}

            </AppTableCell>

            <AppTableCell align="left" sx={{ maxWidth: 240 }}>

              <Tooltip

                title={

                  row.referralCaseProgress

                    ? buildCaseStatusFields(row.referralCaseProgress, (d) =>

                        d ? dayjs(d).format("M/D/YYYY") : null

                      )

                        .filter((f) => f.value)

                        .map((f) => `${f.label}: ${f.value}`)

                        .join("\n") || "No case status recorded"

                    : "No case status recorded"

                }

                placement="top-start"

                enterDelay={400}

              >

                <Typography variant="body2" noWrap component="span">

                  {getCompactCaseStatusLabel(row.referralCaseProgress)}

                </Typography>

              </Tooltip>

            </AppTableCell>

            <AppTableCell align="left">

              {HelpersService.formatTerminationDate(row.terminationDate)}

            </AppTableCell>

            <AppTableCell align="left" sx={{ whiteSpace: "nowrap" }}>

              {row.updatedAt

                ? HelpersService.calcDatesDifference(dayjs.utc(row.updatedAt))

                : "—"}

            </AppTableCell>

            <AppTableCell align="right" onClick={(e) => e.stopPropagation()}>

              <AppButton

                variant="outlined"

                color="primary"

                size="small"

                onClick={() => handleOpenLead(row.id)}

              >

                View

              </AppButton>

            </AppTableCell>

          </AppTableRow>

        ))}

      </AppTable>

    </Box>

  );

}


